// THE CLOSED VOCABULARIES of a staff bug report (`platform_bugs`, plan 101 slice 05,
// #5427). Every list here is STORED — a status, a source, a claim or a wont_fix reason
// lands in a column or a jsonb key a CHECK restates (`packages/db/src/schema/
// platform-bugs.ts`) — so a member added is a migration of that CHECK, and a member
// removed is unreadable rows. `platform-bug.test.ts` pins the set equality.
// Split out of `../platform-bug.ts`, which is the barrel over this directory.

import { z } from 'zod';
import type { SortTerm } from '../../value-objects/list-query/types';

/**
 * A report's life. `screening` and `discarded` are the SCREEN's (the intake screen reads
 * the untrusted title + body before any reader does); the rest are the checkup's.
 *
 *   - `screening`: filed, not yet read by the screen. Never listed, never judged.
 *   - `discarded`: the screen refused it (a `ScreenDiscardReason`). Never listed.
 *   - `open`: screened `ok`, awaiting the checkup.
 *   - `needs_info` / `in_progress` / `fixed` / `wont_fix` / `duplicate`: a verdict.
 */
export const PLATFORM_BUG_STATUSES = [
  'screening',
  'discarded',
  'open',
  'needs_info',
  'in_progress',
  'fixed',
  'wont_fix',
  'duplicate',
] as const;
export const PlatformBugStatusSchema = z.enum(PLATFORM_BUG_STATUSES);
export type PlatformBugStatus = z.infer<typeof PlatformBugStatusSchema>;

/**
 * The statuses a reader may SEE — `open` and later. `bug list` fences on exactly this
 * set, so the nightly checkup never reads a body the screen has not passed.
 */
export const PLATFORM_BUG_LISTED_STATUSES = [
  'open',
  'needs_info',
  'in_progress',
  'fixed',
  'wont_fix',
  'duplicate',
] as const satisfies readonly PlatformBugStatus[];
export const PlatformBugListedStatusSchema = z.enum(PLATFORM_BUG_LISTED_STATUSES);
export type PlatformBugListedStatus = z.infer<typeof PlatformBugListedStatusSchema>;

/** The statuses a verdict may SET. `open` is not one: a verdict always resolves. */
export const PLATFORM_BUG_VERDICT_STATUSES = [
  'needs_info',
  'in_progress',
  'fixed',
  'wont_fix',
  'duplicate',
] as const satisfies readonly PlatformBugListedStatus[];
export type PlatformBugVerdictStatus = (typeof PLATFORM_BUG_VERDICT_STATUSES)[number];

/**
 * Who filed it: developerz.ai staff over MCP (`admin:bugs`), the ONLY producer. A member
 * with no writer is a legacy path in waiting, so there is none; an agent door that files
 * here adds its member (and the CHECK) with the writer, in the same change. Provenance
 * only: it never raises trust — a staff token can leak, so every report is screened and
 * judged on evidence.
 */
export const PLATFORM_BUG_SOURCES = ['staff_mcp'] as const;
export const PlatformBugSourceSchema = z.enum(PLATFORM_BUG_SOURCES);
export type PlatformBugSource = z.infer<typeof PlatformBugSourceSchema>;

/**
 * "Is the report TRUE?" — the checkup's answer in `clean.claim`, the same three words
 * triage grades an outsider's ask with (`TriageClaim`, `packages/agent/src/triage/
 * claim.ts`; restated here because `@developerz/db` cannot import the agent package,
 * and pinned equal by `platform-bug.test.ts`'s literal).
 */
export const PLATFORM_BUG_CLAIMS = ['confirmed', 'refuted', 'unverifiable'] as const;
export const PlatformBugClaimSchema = z.enum(PLATFORM_BUG_CLAIMS);
export type PlatformBugClaim = z.infer<typeof PlatformBugClaimSchema>;

/** Why a report is `wont_fix`. Closed, so the audit can count each per `source`. */
export const PLATFORM_BUG_WONT_FIX_REASONS = [
  'refuted',
  'injection_suspected',
  'by_design',
] as const;
export const PlatformBugWontFixReasonSchema = z.enum(PLATFORM_BUG_WONT_FIX_REASONS);
export type PlatformBugWontFixReason = z.infer<typeof PlatformBugWontFixReasonSchema>;

/** The checkup's confidence in its restatement. Words, not a number a model would invent. */
export const PLATFORM_BUG_CONFIDENCES = ['low', 'medium', 'high'] as const;

export const PLATFORM_BUG_TITLE_MAX = 200;
export const PLATFORM_BUG_BODY_MAX = 20_000;
/** Items per evidence list (`urls`, `session_ids`, `task_ids`) and per `evidence_checked`. */
export const PLATFORM_BUG_EVIDENCE_MAX_ITEMS = 20;
/**
 * The longest id a report's evidence may name. A house id is `<3-letter prefix>_<32 hex>`
 * (36 chars); the brand pattern has no upper bound, so this is what stops one "id" from
 * being a pasted paragraph.
 */
export const PLATFORM_BUG_EVIDENCE_ID_MAX = 64;
/** One free-text field of a verdict (`problem`, `note`, `missing`, …). */
export const PLATFORM_BUG_PROSE_MAX = 2_000;
export const PLATFORM_BUG_REPRO_MAX = 4_000;
export const PLATFORM_BUG_EVIDENCE_CHECKED_ITEM_MAX = 500;

/**
 * The ONE order `bug list` pages in: newest first, `id` as the stable tiebreak the
 * keyset always appends (`buildListClauses`). The MCP verb hands it to `pageQuery`; the
 * store orders by it. One constant, so the two cannot disagree about a cursor.
 */
export const PLATFORM_BUG_LIST_ORDER: readonly SortTerm[] = [{ field: 'created_at', dir: 'desc' }];

/**
 * The `platform_audit_events.kind` values a report's human acts write (that table's
 * `kind` is free text; this is the spelling). `screened` is NOT here: the screen is a
 * machine act, and `platform_audit_events.actor_user_id` names a person — it is
 * `AuditEventKind.PlatformBugScreened` on the worker's platform chain instead.
 */
export const PlatformBugAuditKind = {
  Reported: 'platform_bug.reported',
  Verdict: 'platform_bug.verdict',
} as const;
export type PlatformBugAuditKind = (typeof PlatformBugAuditKind)[keyof typeof PlatformBugAuditKind];
